import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../api/auth';

/**
 * The hero swirl mixing panel.
 *
 * Three colours, each with one strength slider that means exactly what it says,
 * and one speed slider that runs the intuitive way (right = faster). The preview
 * is built from the same --swirl-* variables the marketing site uses, so what
 * you see is what ships. Save writes the theme; the site picks it up within a
 * minute.
 */

interface Swirl {
  bg: string;
  red: string;
  orange: string;
  redAmt: number;
  orangeAmt: number;
  dur1: number;
  dur2: number;
}

const DEFAULT: Swirl = { bg: '#0a0605', red: '#96180e', orange: '#f0782a', redAmt: 0.55, orangeAmt: 0.42, dur1: 42, dur2: 60 };

// One speed number (0 slow … 100 fast) maps to the two rotation times, which
// counter-rotate at a fixed ratio. Right on the slider is always faster.
const durFromSpeed = (v: number) => Math.round(90 - v * 0.72); // 0→90s, 100→18s
const speedFromDur = (d: number) => Math.round((90 - d) / 0.72);

// The preview swirl — the site's rules, scoped to .swirl-preview and using the
// identical --swirl-* variables, so the preview cannot drift from production.
const PREVIEW_CSS = `
.swirl-preview { position: relative; overflow: hidden; border-radius: 12px; height: 300px; color: #fff;
  background:
    radial-gradient(130% 110% at 50% -18%, color-mix(in srgb, var(--swirl-red) calc(46% * var(--swirl-red-amt)), transparent), transparent 52%),
    linear-gradient(180deg, color-mix(in srgb, var(--swirl-bg) 70%, #1a0d08), var(--swirl-bg) 52%, #000); }
.swirl-preview::before, .swirl-preview::after { content: ""; position: absolute; inset: -45%; border-radius: 50%; pointer-events: none; }
.swirl-preview::before {
  background: conic-gradient(from 0deg at 50% 50%, transparent 0deg,
    color-mix(in srgb, var(--swirl-red) 88%, transparent) 45deg, transparent 120deg,
    color-mix(in srgb, var(--swirl-red) 64%, transparent) 165deg, transparent 215deg,
    color-mix(in srgb, var(--swirl-red) 82%, transparent) 262deg, transparent 330deg, transparent 360deg);
  filter: blur(58px); opacity: var(--swirl-red-amt); animation: heroSwirlPrev var(--swirl-dur1) linear infinite; }
.swirl-preview::after {
  background: conic-gradient(from 200deg at 50% 50%, transparent 0deg,
    color-mix(in srgb, var(--swirl-orange) 82%, transparent) 60deg, transparent 130deg,
    color-mix(in srgb, var(--swirl-orange) 58%, transparent) 195deg, transparent 255deg,
    color-mix(in srgb, var(--swirl-orange) 80%, transparent) 305deg, transparent 350deg);
  filter: blur(70px); opacity: var(--swirl-orange-amt); animation: heroSwirlPrev var(--swirl-dur2) linear infinite reverse; }
.swirl-preview .prev-inner { position: relative; z-index: 2; padding: 34px 32px; }
@keyframes heroSwirlPrev { to { transform: rotate(1turn); } }
@media (prefers-reduced-motion: reduce) { .swirl-preview::before, .swirl-preview::after { animation: none; } }
`;

export function HeroSwirlView() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';
  const [s, setS] = useState<Swirl>(DEFAULT);
  const [saved, setSaved] = useState<Swirl>(DEFAULT);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    fetch('/api/site/admin/theme')
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setS({ ...DEFAULT, ...d }); setSaved({ ...DEFAULT, ...d }); } })
      .catch(() => undefined);
  }, []);

  const set = useCallback(<K extends keyof Swirl>(k: K, v: Swirl[K]) => setS((p) => ({ ...p, [k]: v })), []);
  const setSpeed = (v: number) => setS((p) => ({ ...p, dur1: durFromSpeed(v), dur2: Math.round(durFromSpeed(v) * 1.45) }));

  async function save() {
    setBusy(true); setNote(null);
    try {
      const res = await fetch('/api/site/admin/theme', {
        method: 'PUT',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(s),
      });
      if (!res.ok) throw new Error('save failed');
      const clean = await res.json();
      setS({ ...DEFAULT, ...clean }); setSaved({ ...DEFAULT, ...clean });
      setNote('Saved — live on the website within a minute.');
    } catch {
      setNote('Could not save.');
    } finally { setBusy(false); }
  }

  const dirty = JSON.stringify(s) !== JSON.stringify(saved);
  const cssVars = {
    ['--swirl-bg']: s.bg,
    ['--swirl-red']: s.red,
    ['--swirl-orange']: s.orange,
    ['--swirl-red-amt']: String(s.redAmt),
    ['--swirl-orange-amt']: String(s.orangeAmt),
    ['--swirl-dur1']: `${s.dur1}s`,
    ['--swirl-dur2']: `${s.dur2}s`,
  } as React.CSSProperties;

  if (!isAdmin) return <div className="panel muted">The hero mixer requires the ADMIN role.</div>;

  return (
    <div>
      <style>{PREVIEW_CSS}</style>
      <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div>
          <h1 className="view-title">Hero swirl</h1>
          <p className="muted" style={{ marginTop: -6, maxWidth: 640 }}>
            Mix the colours and motion of the marketing site's animated hero. The preview uses the
            same code the site does — what you see is what ships.
          </p>
        </div>
        <div className="row" style={{ gap: 8, alignItems: 'center' }}>
          <button className="btn" onClick={() => setS(DEFAULT)}>Reset to default</button>
          <button className="btn primary" disabled={busy || !dirty} onClick={() => void save()}>
            {busy ? 'Saving…' : dirty ? 'Save & publish' : 'Saved'}
          </button>
        </div>
      </div>

      {note && <div className="panel" style={{ marginBottom: 14, color: dirty ? 'var(--bad)' : 'var(--good)' }}>{note}</div>}

      {/* Live preview */}
      <div className="swirl-preview" style={cssVars}>
        <div className="prev-inner">
          <span
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 18,
              border: '1px solid rgba(255,255,255,.25)', padding: '6px 12px', borderRadius: 999,
              fontFamily: 'var(--mono)', fontSize: 11, letterSpacing: '.16em', textTransform: 'uppercase',
              color: 'rgba(255,255,255,.85)',
            }}
          >
            <span style={{ width: 7, height: 7, borderRadius: '50%', background: s.orange }} /> Line 3 · Live
          </span>
          <div style={{ fontSize: 40, fontWeight: 800, lineHeight: 1.05, fontFamily: 'var(--font-head, inherit)' }}>
            See every defect <span style={{ color: s.orange }}>before it ships.</span>
          </div>
        </div>
      </div>

      {/* Controls — one row per colour: pick the colour, set how strongly it shows. */}
      <div className="panel" style={{ marginTop: 16 }}>
        <ColourRow
          label="Base" hint="The dark ground the swirl floats over — no strength, it is the background."
          colour={s.bg} onColour={(v) => set('bg', v)}
        />
        <ColourRow
          label="Deep colour" hint="Leads the swirl."
          colour={s.red} onColour={(v) => set('red', v)}
          amount={s.redAmt} onAmount={(v) => set('redAmt', v)}
        />
        <ColourRow
          label="Accent" hint="Shows in the moving streaks."
          colour={s.orange} onColour={(v) => set('orange', v)}
          amount={s.orangeAmt} onAmount={(v) => set('orangeAmt', v)}
        />
        <div style={{ borderTop: '1px solid var(--border)', marginTop: 16, paddingTop: 16 }}>
          <label style={{ display: 'block', maxWidth: 420 }}>
            <div className="row" style={{ justifyContent: 'space-between', fontSize: 13, marginBottom: 6 }}>
              <span>Motion speed</span>
              <span className="muted" style={{ fontFamily: 'var(--mono)', fontSize: 12 }}>
                slower ← → faster · {s.dur1}s / {s.dur2}s
              </span>
            </div>
            <input
              type="range" min={0} max={100} step={1}
              value={Math.min(100, Math.max(0, speedFromDur(s.dur1)))}
              onChange={(e) => setSpeed(Number(e.target.value))}
              style={{ width: '100%' }}
            />
          </label>
        </div>
      </div>
    </div>
  );
}

function ColourRow({
  label, hint, colour, onColour, amount, onAmount,
}: {
  label: string; hint: string; colour: string; onColour: (v: string) => void;
  amount?: number; onAmount?: (v: number) => void;
}) {
  return (
    <div
      className="row"
      style={{
        gap: 18, alignItems: 'center', flexWrap: 'wrap',
        padding: '12px 0', borderTop: '1px solid var(--border)',
      }}
    >
      <div style={{ minWidth: 160 }}>
        <div style={{ fontSize: 14, fontWeight: 600 }}>{label}</div>
        <div className="muted" style={{ fontSize: 12 }}>{hint}</div>
      </div>
      <div className="row" style={{ gap: 8, alignItems: 'center' }}>
        <input type="color" value={colour} onChange={(e) => onColour(e.target.value)}
          style={{ width: 46, height: 34, padding: 0, border: '1px solid var(--border)', borderRadius: 6, background: 'none', cursor: 'pointer' }} />
        <input type="text" value={colour} onChange={(e) => onColour(e.target.value)}
          style={{ width: 96, fontFamily: 'var(--mono)', fontSize: 13 }} />
      </div>
      {onAmount !== undefined && amount !== undefined && (
        <label style={{ flex: 1, minWidth: 200 }}>
          <div className="row" style={{ justifyContent: 'space-between', fontSize: 12, marginBottom: 4 }}>
            <span className="muted">Strength</span>
            <span style={{ fontFamily: 'var(--mono)' }}>{Math.round(amount * 100)}%</span>
          </div>
          <input type="range" min={0} max={1} step={0.02} value={amount}
            onChange={(e) => onAmount(Number(e.target.value))} style={{ width: '100%' }} />
        </label>
      )}
    </div>
  );
}
